import React, { useState, useEffect } from 'react';
import HandshakeIcon from '../../../components/HandshakeIcon';
import {
  subscribe,
  getESGLocalizationOverview,
  getRegions,
  getCulturalImpactCategories,
  getLanguageFramework,
  getStakeholderEngagement,
  getComplianceMonitoring,
  getCulturalRiskAssessment,
  getImprovementInitiatives,
  getKPIMonitoring,
  getRegionById,
  getCategoryById,
  calculateCulturalRiskTrends,
  getHighRiskAreas,
  getStakeholdersByRegion,
  updateRegionData,
  updateCategoryCompliance,
  addImprovementInitiative,
  updateInitiativeProgress,
  generateESGLocalizationReport,
  exportESGLocalizationData,
  refreshESGLocalizationData,
  getMediaLocalization,
  getOnsiteEngagement
} from '../../../services/esg/esgLocalizationService';
import { add as addAuditLog } from '../../../services/bread/auditLogService';

const ESGLocalization = () => {
  const [esgLocalizationData, setESGLocalizationData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterCriteria, setFilterCriteria] = useState({
    regionCode: '',
    category: '',
    riskLevel: '',
    complianceThreshold: ''
  });

  useEffect(() => {
    const unsubscribe = subscribe(setESGLocalizationData);
    setLoading(false);
    
    // Log access
    addAuditLog({ 
      user: 'feros.khan@krotrust.org', 
      action: 'page_access', 
      target: 'esg_localization', 
      status: 'success' 
    });

    return () => {
      unsubscribe();
    };
  }, []);

  const handleRefreshData = async () => {
    setLoading(true);
    await refreshESGLocalizationData();
    addAuditLog({ 
      user: 'feros.khan@krotrust.org', 
      action: 'data_refresh', 
      target: 'esg_localization_data', 
      status: 'success' 
    });
    setTimeout(() => setLoading(false), 1000);
  };

  const handleRegionSelect = (region) => {
    setSelectedRegion(region);
    setActiveTab('region-detail');
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setActiveTab('category-detail');
  };

  const handleExportReport = (regionCode = null, categoryId = null) => {
    const reportData = generateESGLocalizationReport(regionCode, categoryId);
    const blob = new Blob([JSON.stringify(reportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `esg_localization_report_${regionCode || categoryId || 'comprehensive'}_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleBulkExport = () => {
    const exportData = exportESGLocalizationData('json', filterCriteria);
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `esg_localization_bulk_export_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading || !esgLocalizationData) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '60px',
            height: '60px',
            border: '4px solid rgba(124, 58, 237, 0.3)',
            borderTop: '4px solid #7c3aed',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }} />
          <p style={{ color: 'white', fontSize: '16px', fontWeight: '600' }}>
            Loading ESG Localization System...
          </p>
        </div>
      </div>
    );
  }

  const overview = getESGLocalizationOverview();
  const regions = getRegions();
  const categories = getCulturalImpactCategories();
  const languageFramework = getLanguageFramework();
  const stakeholderEngagement = getStakeholderEngagement();
  const complianceMonitoring = getComplianceMonitoring();
  const riskAssessment = getCulturalRiskAssessment();
  const initiatives = getImprovementInitiatives();
  const kpis = getKPIMonitoring();
  const trends = calculateCulturalRiskTrends();
  const highRiskAreas = getHighRiskAreas();
  const media = getMediaLocalization();
  const onsite = getOnsiteEngagement();

  // Filter regions based on criteria
  const filteredRegions = regions.filter(region => {
    if (filterCriteria.regionCode && region.code !== filterCriteria.regionCode) return false;
    if (filterCriteria.riskLevel) {
      if (filterCriteria.riskLevel === 'high' && region.culturalRiskScore <= 75) return false;
      if (filterCriteria.riskLevel === 'medium' && (region.culturalRiskScore < 50 || region.culturalRiskScore > 75)) return false;
      if (filterCriteria.riskLevel === 'low' && region.culturalRiskScore >= 50) return false;
    }
    return true;
  });

  // Filter categories based on criteria
  const filteredCategories = categories.filter(category => {
    if (filterCriteria.category && !category.category.toLowerCase().includes(filterCriteria.category.toLowerCase())) return false;
    if (filterCriteria.complianceThreshold && category.globalCompliance < parseInt(filterCriteria.complianceThreshold)) return false;
    return true;
  });

  const OverviewTab = () => (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px' }}>
      {/* Global ESG Localization */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(10px)',
        borderRadius: '20px',
        padding: '24px',
        border: '2px solid rgba(96, 165, 250, 0.3)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
          <span style={{ fontSize: '24px' }}>🌍</span>
          <h3 style={{ fontSize: '18px', fontWeight: '800', color: 'white', margin: 0, textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
            Global ESG Localization
          </h3>
        </div>
        
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <div style={{ fontSize: '48px', fontWeight: '900', color: '#60a5fa', textShadow: '0 0 20px rgba(96, 165, 250, 0.5)' }}>
            {overview.totalRegions}
          </div>
          <div style={{ fontSize: '12px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px' }}>
            Active Regions
          </div>
        </div>
        
        <div style={{ textAlign: 'center', marginBottom: '16px' }}>
          <div style={{ fontSize: '32px', fontWeight: '800', color: '#22c55e' }}>
            {overview.overallCompliance.toFixed(1)}%
          </div>
          <div style={{ fontSize: '11px', fontWeight: '600', color: '#cbd5e1', textTransform: 'uppercase' }}>
            Overall Compliance
          </div>
        </div>
        
        <div style={{ marginTop: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
            <span style={{ fontSize: '12px', fontWeight: '700', color: '#cbd5e1' }}>Language Coverage</span>
            <span style={{ fontSize: '12px', fontWeight: '800', color: '#60a5fa' }}>{overview.languageCoverage.toFixed(1)}%</span>
          </div>
          <div style={{ height: '8px', background: 'rgba(255, 255, 255, 0.1)', borderRadius: '8px', overflow: 'hidden' }}>
            <div style={{ 
              width: `${overview.languageCoverage}%`,
              height: '100%',
              background: 'linear-gradient(90deg, #60a5fa 0%, #3b82f6 100%)',
              borderRadius: '8px'
            }} />
          </div>
        </div>
      </div>

      {/* Cultural Risk Assessment */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(10px)',
        borderRadius: '20px',
        padding: '24px',
        border: '2px solid rgba(251, 191, 36, 0.3)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
          <span style={{ fontSize: '24px' }}>⚠️</span>
          <h3 style={{ fontSize: '18px', fontWeight: '800', color: 'white', margin: 0, textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
            Cultural Risk Assessment
          </h3>
        </div>
        
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <div style={{ fontSize: '42px', fontWeight: '900', color: '#fbbf24', textShadow: '0 0 20px rgba(251, 191, 36, 0.5)' }}>
            {overview.averageCulturalRisk.toFixed(1)}
          </div>
          <div style={{ fontSize: '12px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px' }}>
            Average Risk Score
          </div>
        </div>
        
        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: '600', color: '#cbd5e1' }}>Cultural Sensitivity</span>
            <span style={{ fontSize: '12px', fontWeight: '800', color: '#60a5fa' }}>{overview.culturalSensitivityIndex.toFixed(1)}%</span>
          </div>
          <div style={{ height: '6px', background: 'rgba(255, 255, 255, 0.1)', borderRadius: '6px', overflow: 'hidden' }}>
            <div style={{ 
              width: `${overview.culturalSensitivityIndex}%`,
              height: '100%',
              background: 'linear-gradient(90deg, #60a5fa 0%, #3b82f6 100%)',
              borderRadius: '6px'
            }} />
          </div>
        </div>
        
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
          <span style={{ fontSize: '12px', fontWeight: '600', color: '#cbd5e1' }}>High Risk Areas</span>
          <span style={{ fontSize: '14px', fontWeight: '800', color: '#ef4444' }}>{highRiskAreas.totalHighRiskItems}</span>
        </div>
        
        <div style={{
          padding: '8px 12px',
          background: 'rgba(251, 191, 36, 0.2)',
          border: '1px solid rgba(251, 191, 36, 0.4)',
          borderRadius: '8px'
        }}>
          <span style={{ fontSize: '12px', fontWeight: '700', color: '#fbbf24' }}>⚠️ Risk Level: {riskAssessment.overallRisk}</span>
        </div>
      </div>

      {/* Stakeholder Engagement */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(10px)',
        borderRadius: '20px',
        padding: '24px',
        border: '2px solid rgba(34, 197, 94, 0.3)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            background: 'linear-gradient(135deg, #22c55e 0%, #10b981 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '20px',
            boxShadow: '0 4px 12px rgba(34, 197, 94, 0.4)'
          }}>
            👥
          </div>
          <h3 style={{ fontSize: '18px', fontWeight: '800', color: 'white', margin: 0, textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
            Stakeholder Engagement
          </h3>
        </div>
        
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <div style={{ fontSize: '42px', fontWeight: '900', color: '#22c55e', textShadow: '0 0 20px rgba(34, 197, 94, 0.5)' }}>
            {overview.totalStakeholders.toLocaleString()}
          </div>
          <div style={{ fontSize: '12px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px' }}>
            Total Stakeholders
          </div>
        </div>
        
        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: '600', color: '#cbd5e1' }}>Satisfaction Score</span>
            <span style={{ fontSize: '12px', fontWeight: '800', color: '#22c55e' }}>{overview.stakeholderSatisfaction.toFixed(1)}%</span>
          </div>
          <div style={{ height: '6px', background: 'rgba(255, 255, 255, 0.1)', borderRadius: '6px', overflow: 'hidden' }}>
            <div style={{ 
              width: `${overview.stakeholderSatisfaction}%`,
              height: '100%',
              background: 'linear-gradient(90deg, #22c55e 0%, #10b981 100%)',
              borderRadius: '6px'
            }} />
          </div>
        </div>
        
        {Object.entries(stakeholderEngagement.byCategory).slice(0, 3).map(([key, data], index) => (
          <div key={index} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
            <span style={{ fontSize: '12px', fontWeight: '600', color: '#cbd5e1', textTransform: 'capitalize' }}>{key}</span>
            <span style={{ fontSize: '13px', fontWeight: '800', color: 'white' }}>{data.count}</span>
          </div>
        ))}
      </div>

      {/* Improvement Initiatives */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(10px)',
        borderRadius: '20px',
        padding: '24px',
        border: '2px solid rgba(168, 85, 247, 0.3)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
          <span style={{ fontSize: '24px' }}>📈</span>
          <h3 style={{ fontSize: '18px', fontWeight: '800', color: 'white', margin: 0, textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
            Improvement Initiatives
          </h3>
        </div>
        
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <div style={{ fontSize: '48px', fontWeight: '900', color: '#22c55e', textShadow: '0 0 20px rgba(34, 197, 94, 0.5)' }}>
            {overview.activeInitiatives}
          </div>
          <div style={{ fontSize: '12px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px' }}>
            Active Initiatives
          </div>
        </div>
        
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
          <span style={{ fontSize: '12px', fontWeight: '600', color: '#cbd5e1' }}>Total Budget</span>
          <span style={{ fontSize: '14px', fontWeight: '800', color: '#a855f7' }}>
            ${(initiatives.reduce((sum, i) => sum + i.budget, 0) / 1000000).toFixed(1)}M
          </span>
        </div>
        
        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: '600', color: '#cbd5e1' }}>Avg Progress</span>
            <span style={{ fontSize: '12px', fontWeight: '800', color: '#22c55e' }}>
              {(initiatives.reduce((sum, i) => sum + i.progress, 0) / initiatives.length).toFixed(1)}%
            </span>
          </div>
          <div style={{ height: '8px', background: 'rgba(255, 255, 255, 0.1)', borderRadius: '8px', overflow: 'hidden' }}>
            <div style={{ 
              width: `${(initiatives.reduce((sum, i) => sum + i.progress, 0) / initiatives.length)}%`,
              height: '100%',
              background: 'linear-gradient(90deg, #22c55e 0%, #10b981 100%)',
              borderRadius: '8px'
            }} />
          </div>
        </div>
      </div>

      {/* Regional Risk Matrix */}
      <div className="col-md-8 mb-4">
        <div className="card border-0 shadow-sm">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="fas fa-map me-2"></i>Regional Cultural Risk Assessment
            </h5>
          </div>
          <div className="card-body">
            <div className="row">
              {regions.map((region, index) => (
                <div key={index} className="col-md-6 mb-3">
                  <div className="card h-100 border">
                    <div className="card-body p-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <div>
                          <h6 className="mb-0">{region.name}</h6>
                          <small className="text-muted">{region.code} • {region.countries.length} countries</small>
                        </div>
                        <div className="text-center">
                          <div className={`h5 mb-0 ${
                            region.culturalRiskScore > 75 ? 'text-danger' : 
                            region.culturalRiskScore > 50 ? 'text-warning' : 'text-success'
                          }`}>
                            {region.culturalRiskScore.toFixed(1)}
                          </div>
                          <small className="text-muted">Risk Score</small>
                        </div>
                      </div>
                      <div className="mb-2">
                        <small className="fw-bold">ESG Complexity:</small>
                        <span className={`badge ms-2 ${
                          region.esgComplexity === 'Very High' ? 'bg-danger' :
                          region.esgComplexity === 'High' ? 'bg-warning' : 'bg-success'
                        }`}>
                          {region.esgComplexity}
                        </span>
                      </div>
                      <div className="mb-2">
                        <small className="fw-bold">Primary Languages:</small>
                        <div className="d-flex flex-wrap">
                          {region.primaryLanguages.slice(0, 3).map((lang, idx) => (
                            <span key={idx} className="badge bg-secondary me-1 mb-1">
                              {lang.toUpperCase()}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="row mt-2">
                        <div className="col-4">
                          <div className="text-center">
                            <div className="small fw-bold">{region.culturalSensitivities.indigenous.score}</div>
                            <small className="text-muted">Indigenous</small>
                          </div>
                        </div>
                        <div className="col-4">
                          <div className="text-center">
                            <div className="small fw-bold">{region.culturalSensitivities.workplace.score}</div>
                            <small className="text-muted">Workplace</small>
                          </div>
                        </div>
                        <div className="col-4">
                          <div className="text-center">
                            <div className="small fw-bold">{region.culturalSensitivities.community.score}</div>
                            <small className="text-muted">Community</small>
                          </div>
                        </div>
                      </div>
                      <button 
                        className="btn btn-sm btn-outline-primary w-100 mt-2"
                        onClick={() => handleRegionSelect(region)}
                      >
                        View Details
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* KPI Dashboard */}
      <div className="col-md-4 mb-4">
        <div className="card border-0 shadow-sm h-100">
          <div className="card-header bg-info text-white">
            <h5 className="mb-0">
              <i className="fas fa-chart-line me-2"></i>KPI Monitoring
            </h5>
          </div>
          <div className="card-body">
            {kpis.map((kpi, index) => (
              <div key={index} className="mb-3 p-2 border rounded">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <span className="fw-bold small">{kpi.name}</span>
                  <span className={`badge ${
                    kpi.current >= kpi.target * 0.9 ? 'bg-success' : 
                    kpi.current >= kpi.target * 0.8 ? 'bg-warning' : 'bg-danger'
                  }`}>
                    {kpi.current.toFixed(1)}
                  </span>
                </div>
                <div className="progress mb-1" style={{ height: '6px' }}>
                  <div 
                    className={`progress-bar ${
                      kpi.current >= kpi.target * 0.9 ? 'bg-success' : 
                      kpi.current >= kpi.target * 0.8 ? 'bg-warning' : 'bg-danger'
                    }`}
                    style={{ width: `${Math.min(100, (kpi.current / kpi.target) * 100)}%` }}
                  ></div>
                </div>
                <div className="d-flex justify-content-between">
                  <small className="text-muted">Target: {kpi.target}</small>
                  <small className={`${kpi.change.startsWith('+') ? 'text-success' : 'text-danger'}`}>
                    {kpi.change}
                  </small>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const RegionsTab = () => (
    <div className="row">
      {/* Filter Controls */}
      <div className="col-12 mb-4">
        <div className="card border-0 shadow-sm">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="fas fa-filter me-2"></i>Filter Regions
            </h5>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-3">
                <label className="form-label">Region</label>
                <select 
                  className="form-select"
                  value={filterCriteria.regionCode}
                  onChange={(e) => setFilterCriteria({...filterCriteria, regionCode: e.target.value})}
                >
                  <option value="">All Regions</option>
                  <option value="NA">North America</option>
                  <option value="EU">European Union</option>
                  <option value="APAC">Asia Pacific</option>
                  <option value="LATAM">Latin America</option>
                  <option value="MEA">Middle East & Africa</option>
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label">Risk Level</label>
                <select 
                  className="form-select"
                  value={filterCriteria.riskLevel}
                  onChange={(e) => setFilterCriteria({...filterCriteria, riskLevel: e.target.value})}
                >
                  <option value="">All Risk Levels</option>
                  <option value="high">High Risk (75+)</option>
                  <option value="medium">Medium Risk (50-75)</option>
                  <option value="low">Low Risk (&lt;50)</option>
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label">ESG Complexity</label>
                <select className="form-select">
                  <option value="">Any Complexity</option>
                  <option value="very-high">Very High</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                </select>
              </div>
              <div className="col-md-3">
                <div className="d-flex align-items-end">
                  <button 
                    className="btn btn-outline-secondary me-2"
                    onClick={() => setFilterCriteria({ regionCode: '', category: '', riskLevel: '', complianceThreshold: '' })}
                  >
                    <i className="fas fa-times me-2"></i>Clear
                  </button>
                  <button 
                    className="btn btn-primary"
                    onClick={handleBulkExport}
                  >
                    <i className="fas fa-download me-2"></i>Export
                  </button>
                </div>
              </div>
            </div>
            <div className="row mt-3">
              <div className="col-12">
                <span className="badge bg-info">
                  {filteredRegions.length} regions found
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Regions Table */}
      <div className="col-12">
        <div className="card border-0 shadow-sm">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="fas fa-globe-americas me-2"></i>Regional Cultural Impact Assessment
            </h5>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-hover">
                <thead className="table-light">
                  <tr>
                    <th>Region</th>
                    <th>Countries</th>
                    <th>Risk Score</th>
                    <th>ESG Complexity</th>
                    <th>Indigenous</th>
                    <th>Workplace</th>
                    <th>Community</th>
                    <th>Stakeholders</th>
                    <th>Framework</th>
                    <th>Last Assessment</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRegions.map((region, index) => (
                    <tr key={index}>
                      <td>
                        <div className="fw-bold">{region.name}</div>
                        <small className="text-muted">{region.code}</small>
                      </td>
                      <td>
                        <span className="badge bg-secondary">{region.countries.length}</span>
                        <div className="d-flex flex-wrap">
                          {region.countries.slice(0, 3).map((country, idx) => (
                            <span key={idx} className="badge bg-light text-dark me-1 mb-1">
                              {country}
                            </span>
                          ))}
                          {region.countries.length > 3 && (
                            <span className="badge bg-info">+{region.countries.length - 3}</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="d-flex align-items-center">
                          <span className={`badge me-2 ${
                            region.culturalRiskScore > 75 ? 'bg-danger' : 
                            region.culturalRiskScore > 50 ? 'bg-warning' : 'bg-success'
                          }`}>
                            {region.culturalRiskScore.toFixed(1)}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span className={`badge ${
                          region.esgComplexity === 'Very High' ? 'bg-danger' :
                          region.esgComplexity === 'High' ? 'bg-warning' : 'bg-success'
                        }`}>
                          {region.esgComplexity}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${
                          region.culturalSensitivities.indigenous.score >= 85 ? 'bg-success' : 
                          region.culturalSensitivities.indigenous.score >= 70 ? 'bg-warning' : 'bg-danger'
                        }`}>
                          {region.culturalSensitivities.indigenous.score}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${
                          region.culturalSensitivities.workplace.score >= 85 ? 'bg-success' : 
                          region.culturalSensitivities.workplace.score >= 70 ? 'bg-warning' : 'bg-danger'
                        }`}>
                          {region.culturalSensitivities.workplace.score}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${
                          region.culturalSensitivities.community.score >= 85 ? 'bg-success' : 
                          region.culturalSensitivities.community.score >= 70 ? 'bg-warning' : 'bg-danger'
                        }`}>
                          {region.culturalSensitivities.community.score}
                        </span>
                      </td>
                      <td>{region.localStakeholders.toLocaleString()}</td>
                      <td>
                        <small className="text-muted">{region.regulatoryFramework}</small>
                      </td>
                      <td>{new Date(region.lastAssessment).toLocaleDateString()}</td>
                      <td>
                        <button 
                          className="btn btn-sm btn-outline-primary me-1"
                          onClick={() => handleRegionSelect(region)}
                          title="View Details"
                        >
                          <i className="fas fa-eye"></i>
                        </button>
                        <button 
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => handleExportReport(region.code)}
                          title="Export Report"
                        >
                          <i className="fas fa-download"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const CategoriesTab = () => (
    <div className="row">
      {/* Filter Controls */}
      <div className="col-12 mb-4">
        <div className="card border-0 shadow-sm">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="fas fa-filter me-2"></i>Filter Cultural Impact Categories
            </h5>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-4">
                <label className="form-label">Category</label>
                <input 
                  type="text"
                  className="form-control"
                  placeholder="Search categories..."
                  value={filterCriteria.category}
                  onChange={(e) => setFilterCriteria({...filterCriteria, category: e.target.value})}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Minimum Compliance</label>
                <select 
                  className="form-select"
                  value={filterCriteria.complianceThreshold}
                  onChange={(e) => setFilterCriteria({...filterCriteria, complianceThreshold: e.target.value})}
                >
                  <option value="">No Minimum</option>
                  <option value="90">90% (Excellent)</option>
                  <option value="80">80% (Good)</option>
                  <option value="70">70% (Satisfactory)</option>
                  <option value="60">60% (Needs Improvement)</option>
                </select>
              </div>
              <div className="col-md-4">
                <div className="d-flex align-items-end">
                  <button 
                    className="btn btn-outline-secondary me-2"
                    onClick={() => setFilterCriteria({ regionCode: '', category: '', riskLevel: '', complianceThreshold: '' })}
                  >
                    <i className="fas fa-times me-2"></i>Clear
                  </button>
                  <button 
                    className="btn btn-primary"
                    onClick={handleBulkExport}
                  >
                    <i className="fas fa-download me-2"></i>Export
                  </button>
                </div>
              </div>
            </div>
            <div className="row mt-3">
              <div className="col-12">
                <span className="badge bg-info">
                  {filteredCategories.length} categories found
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Categories Cards */}
      <div className="col-12">
        <div className="row">
          {filteredCategories.map((category, index) => (
            <div key={index} className="col-md-6 mb-4">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-header">
                  <h5 className="mb-0">{category.category}</h5>
                  <small className="text-muted">{category.description}</small>
                </div>
                <div className="card-body">
                  <div className="row mb-3">
                    <div className="col-6">
                      <div className="text-center p-2 border rounded">
                        <div className={`h4 mb-0 ${
                          category.globalCompliance >= 85 ? 'text-success' :
                          category.globalCompliance >= 70 ? 'text-warning' : 'text-danger'
                        }`}>
                          {category.globalCompliance.toFixed(1)}%
                        </div>
                        <small className="text-muted">Global Compliance</small>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="text-center p-2 border rounded">
                        <div className="h4 mb-0 text-info">{category.stakeholderEngagement.toFixed(1)}%</div>
                        <small className="text-muted">Stakeholder Engagement</small>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mb-3">
                    <strong className="small">Key Frameworks:</strong>
                    <div className="d-flex flex-wrap">
                      {category.keyFrameworks.map((framework, idx) => (
                        <span key={idx} className="badge bg-primary me-1 mb-1">
                          {framework}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="mb-3">
                    <strong className="small">Risk Factors:</strong>
                    {category.riskFactors.map((risk, idx) => (
                      <div key={idx} className="d-flex justify-content-between align-items-center mt-1">
                        <small>{risk.factor}</small>
                        <span className={`badge ${
                          risk.severity === 'High' ? 'bg-danger' :
                          risk.severity === 'Medium' ? 'bg-warning' : 'bg-success'
                        }`}>
                          {risk.severity}
                        </span>
                      </div>
                    ))}
                  </div>

                  <div className="d-flex justify-content-between align-items-center">
                    <small className="text-muted">
                      {category.remediationActions} remediation actions
                    </small>
                    <button 
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => handleCategorySelect(category)}
                    >
                      View Details
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const ComplianceTab = () => (
    <div className="row">
      {/* Compliance Overview */}
      <div className="col-md-4 mb-4">
        <div className="card border-0 shadow-sm h-100">
          <div className="card-header bg-success text-white">
            <h5 className="mb-0">
              <i className="fas fa-check-circle me-2"></i>Compliance Overview
            </h5>
          </div>
          <div className="card-body">
            <div className="text-center mb-3">
              <div className="h2 text-success">{complianceMonitoring.overallCompliance.toFixed(1)}%</div>
              <small className="text-muted">Overall Compliance</small>
            </div>
            <div className="mb-2">
              <div className="d-flex justify-content-between">
                <small>Audit Frequency</small>
                <small>{complianceMonitoring.auditFrequency}</small>
              </div>
            </div>
            <div className="mb-2">
              <div className="d-flex justify-content-between">
                <small>Last Assessment</small>
                <small>{new Date(complianceMonitoring.lastComprehensiveAssessment).toLocaleDateString()}</small>
              </div>
            </div>
            <div className="mb-2">
              <div className="d-flex justify-content-between">
                <small>Next Assessment</small>
                <small>{new Date(complianceMonitoring.nextAssessment).toLocaleDateString()}</small>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Framework Compliance */}
      <div className="col-md-8 mb-4">
        <div className="card border-0 shadow-sm">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="fas fa-clipboard-check me-2"></i>Framework Compliance Status
            </h5>
          </div>
          <div className="card-body">
            {Object.entries(complianceMonitoring.frameworkCompliance).map(([framework, data], index) => (
              <div key={index} className="mb-3 p-3 border rounded">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div>
                    <h6 className="mb-0">{framework}</h6>
                    <small className="text-muted">
                      Last Audit: {new Date(data.lastAudit).toLocaleDateString()} | 
                      Next: {new Date(data.nextAudit).toLocaleDateString()}
                    </small>
                  </div>
                  <div className="text-center">
                    <div className={`h4 mb-0 ${
                      data.score >= 90 ? 'text-success' :
                      data.score >= 80 ? 'text-warning' : 'text-danger'
                    }`}>
                      {data.score.toFixed(1)}%
                    </div>
                    <small className="text-muted">Compliance Score</small>
                  </div>
                </div>
                <div className="progress" style={{ height: '8px' }}>
                  <div 
                    className={`progress-bar ${
                      data.score >= 90 ? 'bg-success' :
                      data.score >= 80 ? 'bg-warning' : 'bg-danger'
                    }`}
                    style={{ width: `${data.score}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Risk Assessment Details */}
      <div className="col-md-6 mb-4">
        <div className="card border-0 shadow-sm h-100">
          <div className="card-header bg-warning text-dark">
            <h5 className="mb-0">
              <i className="fas fa-exclamation-triangle me-2"></i>Cultural Risk Assessment
            </h5>
            <small>Overall Risk: {riskAssessment.overallRisk} (Score: {riskAssessment.riskScore})</small>
          </div>
          <div className="card-body">
            {riskAssessment.riskCategories.map((risk, index) => (
              <div key={index} className="mb-3 p-2 border rounded">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <span className="fw-bold small">{risk.category}</span>
                  <span className={`badge ${
                    risk.risk === 'High' ? 'bg-danger' :
                    risk.risk === 'Medium' ? 'bg-warning' : 'bg-success'
                  }`}>
                    {risk.risk}
                  </span>
                </div>
                <div className="progress mb-1" style={{ height: '6px' }}>
                  <div 
                    className={`progress-bar ${
                      risk.risk === 'High' ? 'bg-danger' :
                      risk.risk === 'Medium' ? 'bg-warning' : 'bg-success'
                    }`}
                    style={{ width: `${100 - risk.score}%` }}
                  ></div>
                </div>
                <div className="d-flex justify-content-between">
                  <small className="text-muted">Mitigation: {risk.mitigation}</small>
                  <small className="text-muted">{risk.timeline}</small>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Language Framework */}
      <div className="col-md-6 mb-4">
        <div className="card border-0 shadow-sm h-100">
          <div className="card-header bg-info text-white">
            <h5 className="mb-0">
              <i className="fas fa-language me-2"></i>Language Framework
            </h5>
          </div>
          <div className="card-body">
            <div className="mb-3">
              <strong className="small">Primary Languages:</strong>
              {languageFramework.primaryLanguages.slice(0, 5).map((lang, index) => (
                <div key={index} className="d-flex justify-content-between align-items-center mt-1">
                  <div>
                    <span className="fw-bold">{lang.name}</span>
                    <small className="text-muted ms-2">({lang.code.toUpperCase()})</small>
                  </div>
                  <div className="d-flex align-items-center">
                    <small className="me-2">{lang.coverage.toFixed(1)}%</small>
                    <div className="progress" style={{ width: '60px', height: '4px' }}>
                      <div 
                        className="progress-bar bg-info"
                        style={{ width: `${lang.coverage}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mb-3">
              <strong className="small">Translation Quality:</strong>
              <div className="row mt-2">
                <div className="col-6">
                  <small>Professional: {languageFramework.translationQuality.professional}%</small>
                </div>
                <div className="col-6">
                  <small>Local Review: {languageFramework.translationQuality.localReview}%</small>
                </div>
              </div>
            </div>
            <div>
              <strong className="small">Indigenous Languages:</strong>
              {languageFramework.indigenousLanguages.map((lang, index) => (
                <div key={index} className="d-flex justify-content-between align-items-center mt-1">
                  <small>{lang.name} ({lang.region})</small>
                  <span className={`badge ${
                    lang.status === 'Vulnerable' ? 'bg-warning' :
                    lang.status.includes('Endangered') ? 'bg-danger' : 'bg-info'
                  }`}>
                    {lang.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)'
    }}>
      {/* Hero Header */}
      <div style={{ 
        background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 50%, #c084fc 100%)',
        padding: '60px 40px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <svg style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          opacity: 0.1
        }}>
          <defs>
            <pattern id="esg-pattern" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
              <path d="M 10 0 L 10 60 M 0 10 L 60 10 M 30 0 L 30 60 M 0 30 L 60 30 M 50 0 L 50 60 M 0 50 L 60 50" 
                    stroke="white" strokeWidth="1" fill="none"/>
            </pattern>
          </defs>
          <rect x="0" y="0" width="100%" height="100%" fill="url(#esg-pattern)" />
        </svg>
        
        <div style={{ maxWidth: '1400px', margin: '0 auto', position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '30px' }}>
            <div style={{
              width: '70px',
              height: '70px',
              borderRadius: '20px',
              background: 'rgba(255, 255, 255, 0.15)',
              backdropFilter: 'blur(10px)',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '36px'
            }}>
              🌍
            </div>
            <div>
              <h1 style={{ 
                fontSize: '42px', 
                fontWeight: '900', 
                color: 'white',
                margin: '0 0 10px 0',
                textShadow: '0 2px 4px rgba(0,0,0,0.3)'
              }}>
                ESG Localization
              </h1>
              <p style={{ 
                fontSize: '18px', 
                color: 'rgba(255, 255, 255, 0.95)',
                margin: 0,
                fontWeight: '500'
              }}>
                Cultural and global impact assessment for multinational ESG compliance
              </p>
            </div>
          </div>
          
          <button 
            onClick={handleRefreshData}
            disabled={loading}
            style={{
              padding: '14px 28px',
              fontSize: '15px',
              fontWeight: '700',
              color: 'white',
              background: 'rgba(255, 255, 255, 0.15)',
              backdropFilter: 'blur(10px)',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '12px',
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.target.style.background = 'rgba(255, 255, 255, 0.25)';
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 8px 20px rgba(0,0,0,0.3)';
              }
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.15)';
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = 'none';
            }}
          >
            🔄 Refresh Data
          </button>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div style={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        background: 'rgba(15, 23, 42, 0.95)',
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid rgba(124, 58, 237, 0.3)',
        padding: '20px 40px'
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
          {[
            { id: 'overview', label: 'Overview', icon: '📊' },
            { id: 'regions', label: 'Regional Impact', icon: '🌎' },
            { id: 'categories', label: 'Cultural Categories', icon: '👥' },
            { id: 'compliance', label: 'Compliance & Risk', icon: '🛡️' },
            { id: 'media', label: 'Media', icon: '📡' },
            { id: 'onsite', label: 'Onsite Engagement', icon: 'handshake' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '12px 24px',
                fontSize: '15px',
                fontWeight: '700',
                color: activeTab === tab.id ? 'white' : '#cbd5e1',
                background: activeTab === tab.id 
                  ? 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)'
                  : 'transparent',
                border: activeTab === tab.id 
                  ? '2px solid rgba(124, 58, 237, 0.5)'
                  : '2px solid rgba(124, 58, 237, 0.2)',
                borderRadius: '12px',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => {
                if (activeTab !== tab.id) {
                  e.target.style.background = 'rgba(124, 58, 237, 0.2)';
                  e.target.style.color = 'white';
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== tab.id) {
                  e.target.style.background = 'transparent';
                  e.target.style.color = '#cbd5e1';
                }
              }}
            >
              <span style={{ fontSize: '18px' }}>{tab.icon === 'handshake' ? <HandshakeIcon size={18} /> : tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '40px' }}>
        {activeTab === 'overview' && <OverviewTab />}
        {activeTab === 'regions' && <RegionsTab />}
        {activeTab === 'categories' && <CategoriesTab />}
        {activeTab === 'compliance' && <ComplianceTab />}
        {activeTab === 'media' && (
          <div className="row">
            <div className="col-md-4 mb-4">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-header bg-dark text-white">
                  <h5 className="mb-0"><i className="fas fa-broadcast-tower me-2"></i>Media Localization Coverage</h5>
                </div>
                <div className="card-body">
                  <div className="text-center mb-3">
                    <div className="h2 text-primary">{media.contentCoverage.toFixed(1)}%</div>
                    <small className="text-muted">Priority Assets Localized</small>
                  </div>
                  <div className="mb-2 d-flex justify-content-between">
                    <small>Accessibility (WCAG)</small>
                    <small>{media.accessibilityCompliance.toFixed(1)}%</small>
                  </div>
                  <div className="progress mb-2" style={{height:'6px'}}>
                    <div className="progress-bar bg-success" style={{width:`${media.accessibilityCompliance}%`}}></div>
                  </div>
                  <div className="mb-2 d-flex justify-content-between">
                    <small>Screening Risk Index</small>
                    <small>{media.culturalScreeningRiskIndex.toFixed(1)}</small>
                  </div>
                  <div className="mb-2 d-flex justify-content-between">
                    <small>Misinformation Risk</small>
                    <small>{media.misinformationRiskScore.toFixed(1)}</small>
                  </div>
                  <small className="text-muted">Last Audit: {new Date(media.lastAudit).toLocaleDateString()}</small>
                </div>
              </div>
            </div>
            <div className="col-md-8 mb-4">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-header">
                  <h5 className="mb-0"><i className="fas fa-layer-group me-2"></i>Asset Adaptation & Accessibility</h5>
                </div>
                <div className="card-body">
                  <div className="table-responsive">
                    <table className="table table-sm align-middle">
                      <thead className="table-light">
                        <tr>
                          <th>Asset</th>
                          <th>Type</th>
                          <th>Langs</th>
                          <th>Regions</th>
                          <th>Adaptation</th>
                          <th>Accessibility</th>
                          <th>Issues</th>
                        </tr>
                      </thead>
                      <tbody>
                        {media.assetCatalog.map(asset => (
                          <tr key={asset.id}>
                            <td>{asset.title}</td>
                            <td><span className="badge bg-secondary">{asset.type}</span></td>
                            <td>{asset.languages}</td>
                            <td>{asset.regions}</td>
                            <td>
                              <div className="progress" style={{height:'6px'}}>
                                <div className={`progress-bar ${asset.adaptation>=0.85?'bg-success':asset.adaptation>=0.75?'bg-warning':'bg-danger'}`} style={{width:`${asset.adaptation*100}%`}}></div>
                              </div>
                            </td>
                            <td>
                              <div className="progress" style={{height:'6px'}}>
                                <div className={`progress-bar ${asset.accessibility>=0.9?'bg-success':asset.accessibility>=0.75?'bg-warning':'bg-danger'}`} style={{width:`${asset.accessibility*100}%`}}></div>
                              </div>
                            </td>
                            <td>
                              {asset.flaggedIssues ? <span className="badge bg-danger">{asset.flaggedIssues}</span> : <span className="text-success small">0</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="mt-3">
                    <strong className="small">Regional Adaptation Scores</strong>
                    <div className="d-flex flex-wrap mt-2">
                      {media.adaptationScores.map(r => (
                        <div key={r.region} className="me-3 mb-2">
                          <div className="small fw-bold">{r.region}</div>
                          <div className="progress" style={{width:'80px',height:'6px'}}>
                            <div className="progress-bar bg-primary" style={{width:`${r.score}%`}}></div>
                          </div>
                          <small className="text-muted">{r.score}%</small>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {activeTab === 'onsite' && (
          <div className="row">
            <div className="col-md-4 mb-4">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-header bg-secondary text-white">
                  <h5 className="mb-0"><i className="fas fa-handshake me-2" style={{ color: '#8b5cf6' }}></i>Onsite Program KPIs</h5>
                </div>
                <div className="card-body">
                  <div className="mb-3 text-center">
                    <div className="h2 text-primary">{onsite.totalEventsYTD}</div>
                    <small className="text-muted">Events YTD</small>
                  </div>
                  <div className="mb-2 d-flex justify-content-between"><small>Avg Attendance</small><small>{onsite.avgAttendance}</small></div>
                  <div className="mb-2 d-flex justify-content-between"><small>Local Hiring Rate</small><small>{onsite.localHiringRate.toFixed(1)}%</small></div>
                  <div className="progress mb-2" style={{height:'6px'}}>
                    <div className="progress-bar bg-info" style={{width:`${onsite.localHiringRate}%`}}></div>
                  </div>
                  <div className="mb-2 d-flex justify-content-between"><small>Grievance Resolution</small><small>{onsite.grievanceResolutionRate.toFixed(1)}%</small></div>
                  <div className="progress mb-2" style={{height:'6px'}}>
                    <div className="progress-bar bg-success" style={{width:`${onsite.grievanceResolutionRate}%`}}></div>
                  </div>
                  <div className="mb-2 d-flex justify-content-between"><small>Safety Incidents</small><small className={onsite.safetyIncidents? 'text-danger':'text-success'}>{onsite.safetyIncidents}</small></div>
                  <div className="mb-2 d-flex justify-content-between"><small>Carbon / Event (kg)</small><small>{onsite.carbonPerEventKg.toFixed(1)}</small></div>
                  <div className="mb-2 d-flex justify-content-between"><small>Community Satisfaction</small><small>{onsite.communitySatisfaction.toFixed(1)}%</small></div>
                  <div className="progress mb-2" style={{height:'6px'}}>
                    <div className="progress-bar bg-primary" style={{width:`${onsite.communitySatisfaction}%`}}></div>
                  </div>
                  <div className="small text-muted">
                    QoQ: Events {onsite.lastQuarterChange.events}, Attendance {onsite.lastQuarterChange.attendance}, Carbon {onsite.lastQuarterChange.carbon}, Satisfaction {onsite.lastQuarterChange.satisfaction}
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-4 mb-4">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-header">
                  <h5 className="mb-0"><i className="fas fa-people-arrows me-2"></i>Program Types</h5>
                </div>
                <div className="card-body">
                  {onsite.programTypes.map(p => (
                    <div key={p.type} className="mb-2 p-2 border rounded">
                      <div className="d-flex justify-content-between">
                        <span className="small fw-bold">{p.type}</span>
                        <span className="badge bg-primary">{p.count}</span>
                      </div>
                      <div className="mt-1">
                        <small className="text-muted">Satisfaction {p.satisfaction.toFixed(1)}%</small>
                        <div className="progress" style={{height:'4px'}}>
                          <div className="progress-bar bg-success" style={{width:`${p.satisfaction}%`}}></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="col-md-4 mb-4">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-header">
                  <h5 className="mb-0"><i className="fas fa-calendar-alt me-2"></i>Upcoming Events</h5>
                </div>
                <div className="card-body">
                  {onsite.upcomingEvents.map(evt => (
                    <div key={evt.id} className="mb-3 p-2 border rounded">
                      <div className="d-flex justify-content-between">
                        <span className="small fw-bold">{evt.type}</span>
                        <span className="badge bg-secondary">{evt.region}</span>
                      </div>
                      <div className="d-flex justify-content-between small text-muted mt-1">
                        <span>{new Date(evt.date).toLocaleDateString()}</span>
                        <span>Expected {evt.expectedAttendance}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ESGLocalization;